import { sourceStatus, type KildestatusOppforing } from '../../../theme/theme.ts';
import type { MetricSourceRef, SourceStatus } from '../api/useHypoteseKilder.ts';
import type { MemoryCard } from '../api/useHypoteseHukommelse.ts';
import type { HypothesisConfig, UpdateFrequency } from '../api/useHypotheses.ts';
import { navnAv } from './lesning.ts';
import { kortDato, retningOrd, tall } from './setning.ts';

/**
 * Lesningene H3.3 deler mellom skjemaet, panelet, katalogen og detaljen: kildens status (ikon og
 * ord fra `sourceStatus` i theme.ts, aldri farge alene), oppdateringen, lagringstiden og kortene
 * i «Ligner på». Ingen komponent skriver disse ordene selv.
 */

export function kildestatus(s: SourceStatus): KildestatusOppforing {
  const o = sourceStatus[s];
  if (!o) throw new Error(`[innspel] Ukjent kildestatus «${s}» — theme.ts og kontrakten er ute av synk.`);
  return o;
}

const FREKVENS: Record<UpdateFrequency, string> = { daglig: 'daglig', ukentlig: 'ukentlig', maanedlig: 'månedlig', kvartalsvis: 'kvartalsvis', sjeldnere: 'sjeldnere' };
export const frekvensOrd = (f: UpdateFrequency): string => FREKVENS[f];

/** «90 dager», «1 år», «2 år» — dagene som tjenerens heltall, år når det går opp. */
export function lagringstid(dager: number): string {
  if (dager % 365 === 0) return `${dager / 365} år`;
  return dager === 1 ? '1 dag' : `${dager} dager`;
}

/** Kildekortets meta-linje (W6): «Definisjonen er avklart · oppdateres daglig · eier Samira Haddad · data beholdes i 90 dager». */
export function kildeMeta(k: Pick<MetricSourceRef, 'status' | 'updateFrequency' | 'owner' | 'retentionDays'>): string {
  const deler = [kildestatus(k.status).etikett, ...(k.status === 'maa_bygges' ? [] : [`oppdateres ${frekvensOrd(k.updateFrequency)}`]), `eier ${navnAv(k.owner)}`];
  if (k.retentionDays !== undefined) deler.push(`data beholdes i ${lagringstid(k.retentionDays)}`);
  return deler.join(' · ');
}

/** Rått tall, aldri en rangering (Kh8): «7 hypoteser bruker den», «Ingen bruker den ennå». */
export const brukesAv = (n: number): string => (n === 0 ? 'Ingen bruker den ennå' : n === 1 ? '1 hypotese bruker den' : `${n} hypoteser bruker den`);

/** «Avspillingsloggen er fylt inn. Oppdateres daglig, definisjonen er avklart.» (W7) */
export function fyltInn(k: MetricSourceRef): string {
  const status = k.status === 'verifisert' ? 'definisjonen er avklart' : k.status === 'maa_bygges' ? 'den må bygges' : 'definisjonen er uklar';
  return `${k.name} er fylt inn. Oppdateres ${frekvensOrd(k.updateFrequency)}, ${status}.`;
}

/** «Ligner på tre som er vurdert før» — tallet som ord, som konseptet. */
export function lignerTittel(n: number): string {
  const ord = ['ingen', 'én', 'to', 'tre'][n] ?? String(n);
  return `Ligner på ${ord} som er vurdert før`;
}

const MND = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
/** «4. jan 2027» — årstallet når det ikke er i år. */
function datoMedAarstall(iso: string): string {
  const [aa, mm, dd] = iso.split('-');
  const aar = Number(aa) === new Date().getFullYear() ? '' : ` ${aa}`;
  return `${Number(dd)}. ${MND[Number(mm) - 1] ?? ''}${aar}`;
}

/** Datoen som betyr noe for statusen (W2): tas opp igjen · konkludert · bare datoen. */
export function kortDatoTekst(k: MemoryCard): string {
  if (k.status === 'parkert') return `tas opp igjen ${datoMedAarstall(k.on)}`;
  if (k.status === 'konkludert') return `konkludert ${kortDato(k.on)}`;
  return kortDato(k.on);
}

/** «9 % → 9 % · rett hvis minst 12 %» */
export function resultatOrd(r: NonNullable<MemoryCard['result']>, skille = ' · '): string {
  const e = (v: number) => `${tall(v)} ${r.unit}`.trim();
  return `${r.baselineValue != null ? `${e(r.baselineValue)} → ` : ''}${e(r.resultValue)}${skille}rett hvis ${retningOrd(r.direction)} ${e(r.threshold)}`;
}

/** Det et menneske skrev (W2): navnet med sitatet, resultatet med terskelen, eller koden med setningen. */
export function grunnTekst(k: MemoryCard, config: HypothesisConfig | undefined): string | null {
  if (k.status === 'konkludert' && k.result) return resultatOrd(k.result);
  const sitat = k.decision ? `«${k.decision.text}»` : null;
  if (k.status === 'avvist') {
    const kode = config?.rejectReasonWording.find((w) => w.code === k.rejectReason)?.label;
    return kode && sitat ? `${kode}: ${sitat}` : (kode ?? sitat);
  }
  if (k.status === 'stoppet') {
    const kode = config?.stopReasonWording.find((w) => w.code === k.stopReason)?.label;
    return kode && sitat ? `${kode}: ${sitat}` : (kode ?? sitat);
  }
  return k.decision && sitat ? `${navnAv(k.decision.by)}: ${sitat}` : null;
}

/** Åpne statuser, der «Varsle meg» finnes (tech-decisions 2026-10-29: alle åpne, ikke bare parkert). */
export const kanFoelges = (statusType: string, status: string): boolean => status !== 'utkast' && statusType !== 'complete' && statusType !== 'closed';
